import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';

// Types
interface Company {
  id: number;
  company_name: string;
  address: string | null;
  password: string;
  pancard: string;
  city: string | null;
  state: string | null;
  country: string | null;
  gst_number: string | null;
  gst_uin: string | null;
  code: string | null;
  cin: string | null;
  email: string | null;
  created_at: string;
  updated_at: string;
  HSN_SAC: string;
  GST_SLAB: string | null;
  signature: string;
  prefix: string;
  postfix: string;
  separatorsign: string;
}
interface Sender {
  id: number;
  name: string;
  email: string;
  number: string;
  password: string;
  role: string;
  deletedUser: number;
  createdAt: string;
  updatedAt: string;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  gst_number: string | null;
  pan_number: string | null;
  aadhaar_number: string | null;
  license_number: string | null;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_ifsc: string | null;
  account_holder_name: string | null;
  govt_id_type: string | null;
  govt_id_number: string | null;
  govt_id_document_url: string | null;
  gst_document_url: string | null;
  bank_document_url: string | null;
  profile_status: string;
}
interface Receiver {
  id: number;
  name: string;
  email: string;
  number: string;
  password: string;
  role: string;
  deletedUser: number;
  createdAt: string;
  updatedAt: string;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  gst_number: string | null;
  pan_number: string | null;
  aadhaar_number: string | null;
  license_number: string | null;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_ifsc: string | null;
  account_holder_name: string | null;
  govt_id_type: string | null;
  govt_id_number: string | null;
  govt_id_document_url: string | null;
  gst_document_url: string | null;
  bank_document_url: string | null;
  profile_status: string;
}
interface Vehicle {
  id: number;
  vehicle_number: string;
  transporter_id?: number | null;
  driver_id: number;
  registration_number: string;
  vehicle_type: string | null;
  capacity_kg: number;
  is_available?: boolean | null;
  capacity?: number | null;
  fuel_type?: string | null;
  fitness_expiry?: string | null;
  insurance_expiry?: string | null;
  color?: string | null;
}
interface Driver {
  id: number;
  name: string;
  phone: string;
  user_id?: number | null;
  transporter_id?: number | null;
  license_number: string;
  license_expiry: string;
  current_vehicle_id?: number | null;
  status?: string | null;
  aadhar?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  upi_id?: string | null;
  bank_account_name?: string | null;
  bank_account_number?: string | null;
  bank_ifsc?: string | null;
  bank_name?: string | null;
}
interface Transporter {
  id: number;
  user_id?: number | null;
  company_name: string;
  fleet_size?: number | null;
  license_number: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  gst_number?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  vehicle: Vehicle;
  driver: Driver;
}
interface ConsignmentDetails {
  origin: string;
  destination: string;
  description: string;
}
interface Item {
  name: string;
  qty: string;
  weight: string;
  ratePerQtl: string;
}
interface SubmittedData {
  company: Company | null;
  sender: Sender | null;
  receiver: Receiver | null;
  singletransporter: Transporter | null;
  consignmentDetails: ConsignmentDetails;
  items: Item[];
  paymentType: string;
  date: string;
  invoiceId: string;
  paymentPayableConsignorId?: number;
  subject?: string;
  ewaybillno?: string;
}

type PaymentParty = "sender" | "receiver";

const steps = [
  "Transporter",
  "Company",
  "Sender",
  "Receiver",
  "Details",
  "Items",
  "Payment",
  "Invoice",
];

function generateInvoiceId(origin: string, destination: string, vehicle_number: string | undefined, dateString: string) {
  try {
    let id =
      (origin?.substring(0, 2) || "OR").toUpperCase() +
      (destination?.substring(0, 2) || "DE").toUpperCase() +
      (vehicle_number ? vehicle_number.slice(-4) : "0000") +
      dateString.replace(/-/g, "");
    return id;
  } catch {
    return "LRNO202404";
  }
}

interface InvoicePreviewPanelProps {
  company: Company | null;
  sender: Sender | null;
  receiver: Receiver | null;
  transporter: Transporter | null;
  selectedTransport: any;
  selectedDate: any;
  selectedDriver: any;
  selectedVehicle: any;
  consignmentDetails: ConsignmentDetails;
  items: Item[];
  paymentType: string;
  step: number;
  submittedData: SubmittedData | null;
  subject?: string;
  invoiceNumber?: string;
}

function totalFromItems(items: any[]): string {
  const total = items.reduce((sum, item) => {
    const qty = parseFloat(item.qty);
    const rate = parseFloat(item.ratePerQtl);
    return sum + (isNaN(qty) || isNaN(rate) ? 0 : qty * rate);
  }, 0);
  return total > 0 ? `₹${total.toLocaleString("en-IN")}` : "";
}

function InvoicePreviewPanel({
  company,
  sender,
  receiver,
  transporter,
  selectedDate,
  selectedTransport,
  selectedDriver,
  selectedVehicle,
  consignmentDetails,
  items,
  paymentType,
  step,
  submittedData,
  subject,
  invoiceNumber,
}: InvoicePreviewPanelProps) {
  const formatEntity = (entity: any) => {
    if (!entity) return "—";
    if (typeof entity === "string") return entity;
    if (entity.company_name) return entity.company_name;
    if (entity.address) return entity.address;
    return JSON.stringify(entity);
  };
  const hasContent =
    !!company ||
    !!sender ||
    !!receiver ||
    !!transporter ||
    (!!consignmentDetails &&
      (consignmentDetails.origin ||
        consignmentDetails.destination ||
        consignmentDetails.description));
  if (!hasContent) {
    return (
      <div className="bg-white text-black font-sans text-xs border-2 border-black max-w-3xl mx-auto h-full flex flex-col justify-center items-center py-10">
        <p className="text-xs font-black text-red-600 uppercase tracking-wide">Live Preview</p>
        <p className="text-[11px] text-black mt-1 font-medium">Updates as you fill in</p>
        <p className="text-[13px] text-black/50 italic mt-6">Start filling the form to see a preview here.</p>
      </div>
    );
  }
  const det = consignmentDetails ?? {};
  const safeItems = Array.isArray(items) ? items.filter((i) => i && typeof i === "object" && i.name) : [];
  let paymentLabel: string;
  switch (paymentType) {
    case "paid": paymentLabel = "Paid"; break;
    case "to_pay": paymentLabel = "To Pay"; break;
    case "credit": paymentLabel = "Credit"; break;
    default: paymentLabel = "—";
  }

  // Use manual invoice number if provided, else submitted invoiceId
  const displayLrNo = submittedData?.invoiceId || invoiceNumber?.trim() || "";

  return (
    <div className="bg-white text-black font-sans text-xs border-2 border-black max-w-3xl mx-auto">
      <div className="text-center border-b-2 border-black py-2 px-2">
        <h1 className="text-2xl font-black tracking-widest text-black">{company?.company_name || "COMPANY NAME"}</h1>
        <p className="text-[10px] mt-0.5">{company?.address || "COMPANY ADDRESS"}</p>
      </div>
      <div className="flex border-b border-black">
        <div className="flex-1 px-2 py-1 border-r border-black">
          <span className="text-[9px] text-gray-500 uppercase block">GST Enrol No:</span>
          <span className="font-bold text-[11px]">{company?.gst_number}</span>
        </div>
        <div className="flex-1 px-2 py-1 border-r border-black">
          <span className="text-[9px] text-gray-500 uppercase block">PAN No.</span>
          <span className="font-bold text-[11px]">{company?.pancard}</span>
        </div>
        <div className="px-2 py-1 flex items-end justify-end min-w-[120px]">
          <span className="text-[9px] text-gray-500">AT OWNERS RISK</span>
        </div>
      </div>
      <div className="px-2 py-0.5 text-[10px] border-b border-black">Email Id. - {company?.email}</div>
      <div className="bg-gray-200 text-center text-[9px] uppercase tracking-widest py-0.5 border-b border-black">
        {subject || "Subject to Kishangarh Jurisdiction"}
      </div>
      <div className="flex border-b border-black">
        <div className="flex-1 px-2 py-1.5 border-r border-black">
          <div className="text-[9px] text-gray-500 uppercase mb-0.5">Consignor's / Supplier's Name &amp; Address</div>
          <div className="text-sm font-black">{sender?.name}</div>
          <div className="mt-0.5 text-[11px] text-gray-600">{sender?.address}</div>
        </div>
        <div className="w-64 px-2 py-1.5 text-[11px] space-y-0.5">
          {displayLrNo && (
            <div className="flex items-center gap-1">
              <span className="font-bold whitespace-nowrap">GR NO.:-</span>
              <span>{displayLrNo}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <span className="font-bold whitespace-nowrap">DATED :-</span>
            <span>{selectedDate}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-bold whitespace-nowrap uppercase">{selectedVehicle?.vehicle_type ? selectedVehicle?.vehicle_type : "DEFAULT"} NO.:-</span>
            <span>{selectedVehicle?.vehicle_number ? selectedVehicle?.vehicle_number : "DEFAULT"}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-bold whitespace-nowrap">FROM :-</span>
            <span>{det.origin || "—"}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-bold whitespace-nowrap">To:-</span>
            <span>{det.destination || "—"}</span>
          </div>
        </div>
      </div>
      <div className="flex border-b border-black">
        <div className="flex-1 px-2 py-1.5 border-r border-black">
          <div className="text-[9px] text-gray-500 uppercase mb-0.5">Consignee's / Recipient's Name &amp; Address</div>
          <div className="text-sm font-black">{receiver?.name}</div>
          <div className="mt-0.5 text-[11px] text-gray-600">{receiver?.address || ""}</div>
        </div>
        <div className="w-64 flex items-center justify-center px-3 py-2">
          <p className="text-red-600 font-black text-sm text-center leading-snug">NOTE- NO CASH ONLY CHEQUE OR POD</p>
        </div>
      </div>
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-black px-1 py-1 w-7">#</th>
            <th className="border border-black px-2 py-1 text-left">Product Description</th>
            <th className="border border-black px-1 py-1 w-16">HSN Code</th>
            <th className="border border-black px-1 py-1 w-12">UOM</th>
            <th className="border border-black px-1 py-1 w-14">Qty</th>
            <th className="border border-black px-1 py-1 w-16">Rate</th>
            <th className="border border-black px-1 py-1 w-20">Amount</th>
          </tr>
        </thead>
        <tbody>
          {safeItems.length > 0 ? (
            safeItems.map((item, i) => {
              const qty = parseFloat(item.qty) || 0;
              const rate = parseFloat(item.ratePerQtl) || 0;
              const amount = qty * rate;
              return (
                <tr key={i}>
                  <td className="border border-black px-1 py-1 text-center">{i + 1}</td>
                  <td className="border border-black px-2 py-1">{item.name || "—"}</td>
                  <td className="border border-black px-1 py-1 text-center">—</td>
                  <td className="border border-black px-1 py-1 text-center">Qtl</td>
                  <td className="border border-black px-1 py-1 text-right">{item.qty || "—"}</td>
                  <td className="border border-black px-1 py-1 text-right">{rate > 0 ? `₹${rate.toLocaleString("en-IN")}` : "—"}</td>
                  <td className="border border-black px-1 py-1 text-right">{amount > 0 ? `₹${amount.toLocaleString("en-IN")}` : "—"}</td>
                </tr>
              );
            })
          ) : (
            <>
              {[0, 1, 2, 3].map((i) => (
                <tr key={i} className="h-6">
                  <td className="border border-black px-1 py-1 text-center"></td>
                  <td className="border border-black px-2 py-1 italic text-gray-300">{i === 0 ? "No items added yet" : ""}</td>
                  <td className="border border-black px-1 py-1"></td>
                  <td className="border border-black px-1 py-1"></td>
                  <td className="border border-black px-1 py-1"></td>
                  <td className="border border-black px-1 py-1"></td>
                  <td className="border border-black px-1 py-1"></td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>

      {/* ── TOTALS SECTION ── */}
      <div className="flex border-t border-black">
        <div className="flex-1 px-2 py-2 border-r border-black text-[10px]">
          <p className="font-bold text-[11px] mb-1">GST WILL BE PAID BY CONSIGNEE'S</p>
          <p className="font-bold mb-0.5">1. BANK NAME:- {sender?.bank_name || "—"}</p>
          <p>
            A/C No. {sender?.bank_account_number || "—"} &nbsp; IFSC CODE:- {sender?.bank_ifsc || "—"}
          </p>
        </div>
        <div className="w-56 text-[11px]">
          {[["SUB. Total Amount", totalFromItems(safeItems)], ["ROUND OFF", ""]].map(([label, val]) => (
            <div key={label} className="flex justify-between items-center px-2 py-1 border-b border-gray-300">
              <span>{label}</span>
              <span className="font-bold">{val || ""}</span>
            </div>
          ))}
          <div className="flex justify-between items-center px-2 py-1 border-b-2 border-black font-black">
            <span>Total Amount</span>
            <span>{totalFromItems(safeItems)}</span>
          </div>
          {[
            ["INVOICE NO.:-", displayLrNo],
            ["INVOICE VALUE :-", ""],
            ["DELIVERY AT:-", det.destination || ""],
            ["EWAY BILL NO.", ""],
          ].map(([label, val]) => (
            <div key={label} className="flex justify-between items-center px-2 py-1 border-b border-gray-200">
              <span className="whitespace-nowrap">{label}</span>
              <span className="font-bold text-right">{val}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex border-t border-black">
        <div className="flex-1 px-2 py-2 border-r border-black text-[10px]">
          <p className="font-bold mb-0.5">{subject}</p>
          <p className="font-bold mb-0.5">TERMS &amp; CONDITION :-</p>
          <p className="text-gray-500">CARRIERS ARE NOT RESPONSIBLE FOR LEAKAGE, BREAKAGE, WATER DAMAGES.</p>
          <p className="font-bold mt-1">E.&amp;O.E.</p>
        </div>
        <div className="w-56 px-3 py-2 text-left text-[10px]">
          <div className="h-14" />
          <div style={{ textAlign: "right" }}>
            <img src={`${process.env.REACT_APP_API_URL}${company?.signature}`} alt="Company Signature" style={{ display: "inline-block" }} />
          </div>
          <p className="font-bold">{formatEntity(transporter)}</p>
          <p>For - {formatEntity(company) || "COMPANY NAME"}</p>
          <p className="font-bold">Authorised signatory</p>
        </div>
      </div>
      <div className="flex border-t border-black">
        <div className="flex-1 px-2 py-1.5 border-r border-black text-[10px]">
          <span className="font-bold">PAYMENT MODE: </span>
          <span className={`ml-1 font-bold px-2 py-0.5 rounded ${paymentType === "paid" ? "bg-black text-white" : paymentType === "to_pay" ? "border border-black text-black" : paymentType === "credit" ? "border border-black text-black" : "border border-black/20 text-black"}`}>
            {paymentLabel}
          </span>
        </div>
        {step === 7 && submittedData && (
          <div className="px-2 py-1.5 text-[10px] text-right">
            <span className="font-bold text-green-700">✓ Invoice Generated</span>
            <p className="font-mono text-[10px]">{submittedData.invoiceId}</p>
          </div>
        )}
      </div>
      {(() => {
        function numberToWords(num: number): string {
          if (num === 0) return "Zero";
          const a = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
          const b = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
          const g = ["","Thousand","Lakh","Crore"];
          function toWords(num: number): string {
            if (num < 20) return a[num];
            if (num < 100) return b[Math.floor(num / 10)] + (num % 10 ? " " + a[num % 10] : "");
            if (num < 1000) return a[Math.floor(num / 100)] + " Hundred" + (num % 100 ? " " + toWords(num % 100) : "");
            const chunks: number[] = [];
            chunks.push(num % 1000); let n = Math.floor(num / 1000);
            while (n > 0) { chunks.push(n % 100); n = Math.floor(n / 100); }
            let res = '';
            for (let i = chunks.length - 1; i >= 0; i--)
              if (chunks[i]) res += (res ? " " : "") + toWords(chunks[i]) + (g[i] ? " " + g[i] : "");
            return res.trim();
          }
          const parts = num.toFixed(2).split(".");
          let wordStr = toWords(parseInt(parts[0]));
          if (parseInt(parts[1], 10) > 0) wordStr += " and " + toWords(parseInt(parts[1], 10)) + " Paise";
          return wordStr;
        }
        const grandTotal = safeItems.reduce((sum, item) => {
          const qty = parseFloat(item.qty) || 0;
          const rate = parseFloat(item.ratePerQtl) || 0;
          return sum + qty * rate;
        }, 0);
        return (
          <div className="px-2 py-1 border-t border-black text-[10px]">
            <span className="font-bold">AMOUNT IN WORDS :- </span>
            <span className="italic text-gray-500">
              {grandTotal > 0 ? numberToWords(grandTotal).toUpperCase() + "  ONLY /-" : ""}
            </span>
          </div>
        );
      })()}
      <div className="text-center text-[9px] text-gray-500 py-1 border-t border-black">
        THIS IS COMPUTER GENERATED LR. DOES NOT REQUIRE SIGN.
      </div>
    </div>
  );
}

export const AddConsignment: React.FC = () => {
  const [step, setStep] = useState(0);
  const [company, setCompany] = useState<any>(null);
  const [sender, setSender] = useState<any>(null);
  const [receiver, setReceiver] = useState<any>(null);
  const [selectedTransporterMock, setSelectedTransporterMock] = useState<any | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<any | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<any | null>(null);
  const [singletransporter, setSingleTransporter] = useState<any>(null);
  const [consignmentDetails, setConsignmentDetails] = useState<any>({ origin: "", destination: "", description: "" });
  const [items, setItems] = useState<Item[]>([{ name: "", qty: "", weight: "", ratePerQtl: "" }]);
  const [paymentType, setPaymentType] = useState("paid");
  const [submittedData, setSubmittedData] = useState<any>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const previewPanelRef = useRef<HTMLDivElement>(null);
  const [subject, setSubject] = useState<string>("");
  const [paymentPayableConsignor, setPaymentPayableConsignor] = useState<"sender" | "receiver">("sender");
  const [companySearch, setCompanySearch] = useState("");
  const [senderSearch, setSenderSearch] = useState("");
  const [receiverSearch, setReceiverSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [hasBroker, setHasBroker] = useState(false);
  const [brokerPaidBy, setBrokerPaidBy] = useState<"receiver" | "sender" | null>(null);

  // ✅ NEW: Manual invoice number state
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");

  const [companiesMock, setCompanies] = useState<any[]>([]);
  const [sendersMock, setSenders] = useState<any[]>([]);
  const [receiversMock, setReceivers] = useState<any[]>([]);
  const [transporters, setTransporters] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/auth/transporters`).then(res => setTransporters(res.data));
    axios.get(`${process.env.REACT_APP_API_URL}/auth/drivers`).then(res => setDrivers(res.data));
    axios.get(`${process.env.REACT_APP_API_URL}/auth/vehicles`).then(res => setVehicles(res.data));
    const fetchSenders = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/alluser/consignor`);
        setSenders(response.data || []);
      } catch { setSenders([]); }
    };
    const fetchReceivers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/alluser/consignor`);
        setReceivers(response.data || []);
      } catch { setReceivers([]); }
    };
    fetchSenders();
    fetchReceivers();
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/companyAuth/companies`);
        setCompanies(
          response.data.map((company: any) => ({
            ...company,
            id: company.id ?? company.company_id,
            company_name: company.company_name ?? company.name ?? "",
            address: company.address ?? null,
            password: company.password ?? "",
            pancard: company.pancard ?? "",
            city: company.city ?? company.company_city ?? null,
            state: company.state ?? null,
            country: company.country ?? null,
            gst_number: company.gst_number ?? company.company_gst ?? company.gst ?? null,
            gst_uin: company.gst_uin ?? null,
            code: company.code ?? null,
            cin: company.cin ?? null,
            email: company.email ?? null,
            created_at: company.created_at ?? "",
            updated_at: company.updated_at ?? "",
            HSN_SAC: company.HSN_SAC ?? "",
            GST_SLAB: company.GST_SLAB ?? null,
            signature: company.signature ?? "",
            prefix: company.prefix ?? "",
            postfix: company.postfix ?? "",
            separatorsign: company.separatorsign ?? "",
          }))
        );
      } catch { setCompanies([]); }
    };
    fetchCompanies();
  }, []);

  const vehiclesForDriver = vehicles;

  const isStepValid = () => {
    switch (step) {
      case 0: return !!selectedVehicle && !!selectedDriver;
      case 1: return !!company;
      case 2: return !!sender;
      case 3: return !!receiver;
      case 4: return !!consignmentDetails.origin && !!consignmentDetails.destination;
      case 5: return !!items[0]?.name;
      case 6: return !!paymentType && paymentPayableConsignor;
      default: return true;
    }
  };

  const S = StyleSheet.create({
    page: { fontFamily: 'Helvetica', fontSize: 9, color: '#000', backgroundColor: '#fff', border: '2px solid black', padding: '12px' },
    header: { borderBottom: '2px solid black', paddingVertical: 5, paddingHorizontal: 8, alignItems: 'center' },
    companyName: { fontSize: 20, fontFamily: 'Helvetica-Bold', letterSpacing: 2 },
    companyAddress: { fontSize: 8, marginTop: 2 },
    row: { flexDirection: 'row' },
    gstRow: { flexDirection: 'row', borderBottom: '1px solid black' },
    gstCell: { flex: 1, paddingHorizontal: 6, paddingVertical: 3, borderRight: '1px solid black' },
    gstLabel: { fontSize: 7, color: '#888', textTransform: 'uppercase', marginBottom: 1 },
    gstValue: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
    ownersRisk: { paddingHorizontal: 6, paddingVertical: 3, minWidth: 90, alignItems: 'flex-end', justifyContent: 'flex-end' },
    ownersRiskText: { fontSize: 7, color: '#888' },
    emailRow: { borderBottom: '1px solid black', paddingHorizontal: 6, paddingVertical: 2 },
    emailText: { fontSize: 8 },
    banner: { backgroundColor: '#e5e5e5', borderBottom: '1px solid black', paddingVertical: 2, alignItems: 'center' },
    bannerText: { fontSize: 7, letterSpacing: 1, textTransform: 'uppercase' },
    consignBlock: { flexDirection: 'row', borderBottom: '1px solid black' },
    consignLeft: { flex: 1, paddingHorizontal: 6, paddingVertical: 5, borderRight: '1px solid black' },
    consignLabel: { fontSize: 7, color: '#888', textTransform: 'uppercase', marginBottom: 2 },
    consignName: { fontSize: 11, fontFamily: 'Helvetica-Bold' },
    consignAddress: { fontSize: 8, color: '#555', marginTop: 2 },
    consignRight: { width: 170, paddingHorizontal: 6, paddingVertical: 5 },
    detailRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 2 },
    detailLabel: { fontFamily: 'Helvetica-Bold', fontSize: 8, marginRight: 3 },
    detailValue: { fontSize: 8 },
    noteBox: { width: 170, paddingHorizontal: 8, paddingVertical: 5, justifyContent: 'center', alignItems: 'center' },
    noteText: { color: 'red', fontFamily: 'Helvetica-Bold', fontSize: 9, textAlign: 'center', lineHeight: 1.3 },
    table: { width: '100%' },
    tableHeaderRow: { flexDirection: 'row', backgroundColor: '#e5e5e5' },
    tableRow: { flexDirection: 'row' },
    th: { border: '1px solid black', paddingHorizontal: 4, paddingVertical: 3, fontFamily: 'Helvetica-Bold', fontSize: 8, textAlign: 'center' },
    td: { border: '1px solid black', paddingHorizontal: 4, paddingVertical: 3, fontSize: 8 },
    totalsRow: { flexDirection: 'row', borderTop: '1px solid black' },
    totalsLeft: { flex: 1, borderRight: '1px solid black', paddingHorizontal: 6, paddingVertical: 5 },
    totalsRight: { width: 170 },
    totalLineRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 2, borderBottom: '1px solid #ccc' },
    totalLineBold: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 3, borderBottom: '2px solid black' },
    totalLineLight: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 2, borderBottom: '1px solid #eee' },
    totalLabel: { fontSize: 8 },
    totalValue: { fontSize: 8, fontFamily: 'Helvetica-Bold' },
    footerRow: { flexDirection: 'row', borderTop: '1px solid black' },
    footerLeft: { flex: 1, borderRight: '1px solid black', paddingHorizontal: 6, paddingVertical: 5 },
    footerRight: { width: 170, paddingHorizontal: 8, paddingVertical: 5, alignItems: 'flex-end' },
    paymentRow: { flexDirection: 'row', borderTop: '1px solid black', paddingHorizontal: 6, paddingVertical: 3, alignItems: 'center' },
    amountWordsRow: { borderTop: '1px solid black', paddingHorizontal: 6, paddingVertical: 3 },
    computerNote: { borderTop: '1px solid black', paddingVertical: 3, alignItems: 'center' },
    bold: { fontFamily: 'Helvetica-Bold' },
    gray: { color: '#888' },
    small: { fontSize: 7 },
    red: { color: 'red' },
  });

  function numberToWords(num: number): string {
    if (!num || num === 0) return '';
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const g = ['', 'Thousand', 'Lakh', 'Crore'];
    function toW(n: number): string {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
      if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + toW(n % 100) : '');
      const chunks: number[] = [];
      chunks.push(n % 1000); let nn = Math.floor(n / 1000);
      while (nn > 0) { chunks.push(nn % 100); nn = Math.floor(nn / 100); }
      let res = '';
      for (let i = chunks.length - 1; i >= 0; i--)
        if (chunks[i]) res += (res ? ' ' : '') + toW(chunks[i]) + (g[i] ? ' ' + g[i] : '');
      return res.trim();
    }
    const parts = num.toFixed(2).split('.');
    let str = toW(parseInt(parts[0]));
    if (parseInt(parts[1]) > 0) str += ' and ' + toW(parseInt(parts[1])) + ' Paise';
    return str.toUpperCase() + ' ONLY /-';
  }

  function InvoicePDF({ company, sender, receiver, selectedVehicle, consignmentDetails, items, paymentType, submittedData, subject, transporter }: any) {
    const safeItems = Array.isArray(items) ? items.filter((i: any) => i?.name) : [];
    const total = safeItems.reduce((sum: number, item: any) => sum + (parseFloat(item.qty) || 0) * (parseFloat(item.ratePerQtl) || 0), 0);
    const paymentLabel = paymentType === 'paid' ? 'Paid' : paymentType === 'to_pay' ? 'To Pay' : 'Credit';
    const colFlex = [0.4, 3, 1, 0.8, 0.8, 0.8, 1];
    const colHeaders = ['#', 'Product Description', 'HSN Code', 'UOM', 'Qty', 'Rate', 'Amount'];
    const colAlign: ('left' | 'center' | 'right')[] = ['center', 'left', 'center', 'center', 'right', 'right', 'right'];
    const displayItems = safeItems.length > 0
      ? safeItems
      : [{ name: '', qty: '', ratePerQtl: '' }, { name: '', qty: '', ratePerQtl: '' }, { name: '', qty: '', ratePerQtl: '' }, { name: '', qty: '', ratePerQtl: '' }];

    return (
      <Document>
        <Page size="A4" style={S.page}>
          <View style={S.header}>
            <Text style={S.companyName}>{company?.company_name || 'COMPANY NAME'}</Text>
            <Text style={S.companyAddress}>{company?.address || 'COMPANY ADDRESS'}</Text>
          </View>
          <View style={S.gstRow}>
            <View style={S.gstCell}><Text style={S.gstLabel}>GST Enrol No:</Text><Text style={S.gstValue}>{company?.gst_number || '—'}</Text></View>
            <View style={S.gstCell}><Text style={S.gstLabel}>PAN No.</Text><Text style={S.gstValue}>{company?.pancard || '—'}</Text></View>
            <View style={S.ownersRisk}><Text style={S.ownersRiskText}>AT OWNERS RISK</Text></View>
          </View>
          <View style={S.emailRow}><Text style={S.emailText}>Email Id. - {company?.email || ''}</Text></View>
          <View style={S.banner}><Text style={S.bannerText}>{subject || 'Subject to Kishangarh Jurisdiction'}</Text></View>
          <View style={S.consignBlock}>
            <View style={S.consignLeft}>
              <Text style={S.consignLabel}>Consignor's / Supplier's Name & Address</Text>
              <Text style={S.consignName}>{sender?.name || '—'}</Text>
              <Text style={S.consignAddress}>{sender?.address || ''}</Text>
            </View>
            <View style={S.consignRight}>
              {submittedData?.invoiceId && <View style={S.detailRow}><Text style={S.detailLabel}>GR NO.:-</Text><Text style={S.detailValue}>{submittedData.invoiceId}</Text></View>}
              <View style={S.detailRow}><Text style={S.detailLabel}>DATED :-</Text><Text style={S.detailValue}>{submittedData?.date ? new Date(submittedData.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}</Text></View>
              <View style={S.detailRow}><Text style={S.detailLabel}>{(selectedVehicle?.vehicle_type || 'TRUCK').toUpperCase()} NO.:-</Text><Text style={S.detailValue}>{selectedVehicle?.vehicle_number || '—'}</Text></View>
              <View style={S.detailRow}><Text style={S.detailLabel}>FROM :-</Text><Text style={S.detailValue}>{consignmentDetails?.origin || '—'}</Text></View>
              <View style={S.detailRow}><Text style={S.detailLabel}>To:-</Text><Text style={S.detailValue}>{consignmentDetails?.destination || '—'}</Text></View>
            </View>
          </View>
          <View style={S.consignBlock}>
            <View style={S.consignLeft}>
              <Text style={S.consignLabel}>Consignee's / Recipient's Name & Address</Text>
              <Text style={S.consignName}>{receiver?.name || '—'}</Text>
              <Text style={S.consignAddress}>{receiver?.address || ''}</Text>
            </View>
            <View style={S.noteBox}><Text style={S.noteText}>NOTE- NO CASH ONLY CHEQUE OR POD</Text></View>
          </View>
          <View style={S.table}>
            <View style={S.tableHeaderRow}>
              {colHeaders.map((h, i) => (
                <View key={h} style={[S.th, { flex: colFlex[i], textAlign: colAlign[i] }]}><Text>{h}</Text></View>
              ))}
            </View>
            {displayItems.map((item: any, i: number) => {
              const qty = parseFloat(item.qty) || 0;
              const rate = parseFloat(item.ratePerQtl) || 0;
              const amount = qty * rate;
              return (
                <View key={i} style={S.tableRow}>
                  <View style={[S.td, { flex: colFlex[0], textAlign: 'center' }]}><Text>{item.name ? String(i + 1) : ''}</Text></View>
                  <View style={[S.td, { flex: colFlex[1], textAlign: 'left' }]}><Text>{item.name || ''}</Text></View>
                  <View style={[S.td, { flex: colFlex[2], textAlign: 'center' }]}><Text>{item.name ? '—' : ''}</Text></View>
                  <View style={[S.td, { flex: colFlex[3], textAlign: 'center' }]}><Text>{item.name ? 'Qtl' : ''}</Text></View>
                  <View style={[S.td, { flex: colFlex[4], textAlign: 'right' }]}><Text>{item.qty || ''}</Text></View>
                  <View style={[S.td, { flex: colFlex[5], textAlign: 'right' }]}><Text>{rate > 0 ? `₹${rate}` : ''}</Text></View>
                  <View style={[S.td, { flex: colFlex[6], textAlign: 'right' }]}><Text>{amount > 0 ? `₹${amount.toLocaleString('en-IN')}` : ''}</Text></View>
                </View>
              );
            })}
          </View>
          <View style={S.totalsRow}>
            <View style={S.totalsLeft}>
              <Text style={[S.bold, { fontSize: 9, marginBottom: 3 }]}>GST WILL BE PAID BY CONSIGNEE'S</Text>
              <Text style={[S.bold, { fontSize: 8, marginBottom: 2 }]}>
                1. BANK NAME:- {sender?.bank_name || '—'}
              </Text>
              <Text style={{ fontSize: 8 }}>
                A/C No. {sender?.bank_account_number || '—'}{'   '}IFSC CODE:- {sender?.bank_ifsc || '—'}
              </Text>
            </View>
            <View style={S.totalsRight}>
              <View style={S.totalLineRow}><Text style={S.totalLabel}>SUB. Total Amount</Text><Text style={S.totalValue}>{total > 0 ? `₹${total.toLocaleString('en-IN')}` : ''}</Text></View>
              <View style={S.totalLineRow}><Text style={S.totalLabel}>ROUND OFF</Text><Text style={S.totalValue}></Text></View>
              <View style={S.totalLineBold}><Text style={[S.totalLabel, S.bold]}>Total Amount</Text><Text style={[S.totalValue, S.bold]}>{total > 0 ? `₹${total.toLocaleString('en-IN')}` : ''}</Text></View>
              <View style={S.totalLineLight}><Text style={S.totalLabel}>INVOICE NO.:-</Text><Text style={S.totalValue}>{submittedData?.invoiceId || ''}</Text></View>
              <View style={S.totalLineLight}><Text style={S.totalLabel}>INVOICE VALUE :-</Text><Text style={S.totalValue}></Text></View>
              <View style={S.totalLineLight}><Text style={S.totalLabel}>DELIVERY AT:-</Text><Text style={S.totalValue}>{consignmentDetails?.destination || ''}</Text></View>
              <View style={S.totalLineLight}><Text style={S.totalLabel}>EWAY BILL NO.</Text><Text style={S.totalValue}></Text></View>
            </View>
          </View>
          <View style={S.footerRow}>
            <View style={S.footerLeft}>
              <Text style={[S.bold, { fontSize: 8, marginBottom: 2 }]}>SUBJECT TO KISHANGARH JURISDICTION</Text>
              <Text style={[S.bold, { fontSize: 8, marginBottom: 2 }]}>TERMS & CONDITION :-</Text>
              <Text style={[S.gray, { fontSize: 8, marginBottom: 4 }]}>CARRIERS ARE NOT RESPONSIBLE FOR LEAKAGE, BREAKAGE, WATER DAMAGES.</Text>
              <Text style={[S.bold, { fontSize: 8 }]}>E.&O.E.</Text>
            </View>
            <View style={S.footerRight}>
              <Text style={[S.bold, { fontSize: 8, marginBottom: 2 }]}>For {company?.company_name || 'COMPANY NAME'}</Text>
              <View style={{ height: 36 }}>
                {company?.signature ? <Image src={`${process.env.REACT_APP_API_URL}${company.signature}`} style={{ maxHeight: 36, objectFit: 'contain' }} /> : null}
              </View>
              <Text style={[S.bold, { fontSize: 8 }]}>{transporter?.company_name || '—'}</Text>
              <Text style={{ fontSize: 8 }}>For - {company?.company_name || 'COMPANY NAME'}</Text>
              <Text style={[S.bold, { fontSize: 8 }]}>Authorised signatory</Text>
            </View>
          </View>
          <View style={S.paymentRow}>
            <Text style={[S.bold, { fontSize: 8 }]}>PAYMENT MODE: </Text>
            <Text style={[S.bold, { fontSize: 8, marginLeft: 4, paddingHorizontal: 6, paddingVertical: 1, backgroundColor: paymentType === 'paid' ? '#000' : '#fff', color: paymentType === 'paid' ? '#fff' : '#000', border: '1px solid black' }]}>{paymentLabel}</Text>
          </View>
          <View style={S.amountWordsRow}>
            <Text style={{ fontSize: 8 }}>
              <Text style={S.bold}>AMOUNT IN WORDS :- </Text>
              <Text style={[S.gray, { fontStyle: 'italic' }]}>{total > 0 ? numberToWords(total) : ''}</Text>
            </Text>
          </View>
          <View style={S.computerNote}><Text style={[S.gray, S.small]}>THIS IS COMPUTER GENERATED LR. DOES NOT REQUIRE SIGN.</Text></View>
        </Page>
      </Document>
    );
  }

  async function generatePdfAndSendToServer(invoiceId: string): Promise<string> {
    const blob = await pdf(
      <InvoicePDF
        company={company}
        sender={sender}
        receiver={receiver}
        selectedVehicle={selectedVehicle}
        consignmentDetails={consignmentDetails}
        items={items}
        paymentType={paymentType}
        submittedData={{ ...submittedData, invoiceId, date: selectedDate }}
        subject={subject}
        transporter={singletransporter}
      />
    ).toBlob();
    const filename = `invoice_${invoiceId}.pdf`;
    try {
      const formData = new FormData();
      formData.append("invoicePdf", blob, filename);
      formData.append("invoiceId", invoiceId);
      const uploadResp = await axios.post(`${process.env.REACT_APP_API_URL}/invoiceapi/uploadpdf`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const link = uploadResp.data?.link ? `${process.env.REACT_APP_API_URL}${uploadResp.data.link}` : "";
      if (link) setPdfUrl(link);
      return link;
    } catch {
      alert("Failed to upload invoice PDF to server.");
      return "";
    }
  }

  async function submitConsignmentToApiAndPdf() {
    // ✅ Use manual invoice number if provided, otherwise auto-generate
    const invoiceId = invoiceNumber.trim()
      ? invoiceNumber.trim()
      : generateInvoiceId(consignmentDetails.origin, consignmentDetails.destination, selectedVehicle?.vehicle_number, selectedDate);

    const ewaybillno = invoiceId;
    let totoalamount = totalFromItems(items);
    let paymentPayableConsignorId = paymentPayableConsignor === "sender" ? sender?.id : receiver?.id;
    const link = await generatePdfAndSendToServer(invoiceId);
    if (!link) return;
    let itemDescriptionsIds: number[] = [];
    for (const item of items) {
      if (!item.name) continue;
      try {
        const computedAmount = (parseFloat(item.qty) || 0) * (parseFloat(item.ratePerQtl) || 0);
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/invoiceapi/invoice_items`, {
          invoiceId, description: item.name, hsm_code: "-", uom: "Qtl", qty: item.qty, amount: computedAmount.toString()
        });
        if (res.data && typeof res.data.insertId === "number") itemDescriptionsIds.push(res.data.insertId);
      } catch (e) { alert("Failed to add item '" + item.name + "' to invoice_items!"); }
    }

    // ✅ Resolve broker_id: the user ID of whoever is paying the broker
    const brokerUserId =
      hasBroker && brokerPaidBy === "sender"
        ? sender?.id
        : hasBroker && brokerPaidBy === "receiver"
        ? receiver?.id
        : null;

    const invoicePayload = {
      company: company?.id,
      sender: sender?.id,
      receiver: receiver?.id,
      payment: totoalamount,
      transporter: singletransporter?.id || selectedTransporterMock?._rawTransporter?.id,
      driver: selectedDriver?.id,
      vehicles: selectedVehicle?.id,
      source: consignmentDetails.origin,
      destination: consignmentDetails.destination,
      description: consignmentDetails.description,
      receiverAmountStatatus: "Pending",
      senderAmountStatatus: "Pending",
      transporterAmountStatatus: "Pending",
      subjects: subject,
      InvoiceNo: invoiceId,
      EWAYBILLNO: ewaybillno,
      paymentType,
      paymentPayableConsignor: paymentPayableConsignorId,
      date: selectedDate,
      subject,
      paymentStatus: paymentType,
      ItemsDescription: JSON.stringify(itemDescriptionsIds),
      link,
      // ✅ Broker fields — null when no broker involved
      broker: brokerUserId,
      broker_paid_by: hasBroker ? brokerPaidBy : null,
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/invoiceapi/invoice`, invoicePayload);
    } catch (error) {
      alert("Failed to create invoice/consignment\n" + (error && (error as any)?.message));
      return false;
    }
    setSubmittedData({ company, sender, receiver, singletransporter, consignmentDetails, items, paymentType, date: selectedDate, invoiceId, paymentPayableConsignorId, subject, ewaybillno });
    await new Promise(r => setTimeout(r, 200));
    setPdfUrl(link);
    setShowInvoiceModal(true);
    return true;
  }

  const handleNext = async () => {
    if (step === 0 && selectedVehicle && selectedDriver) {
      const owningTransporter = transporters.find(t => t.id === selectedVehicle.transporter_id) || null;
      setSingleTransporter(owningTransporter ? {
        ...owningTransporter,
        vehicle: { id: selectedVehicle.id, registration_number: selectedVehicle.registration_number, vehicle_number: selectedVehicle.vehicle_number, vehicle_type: selectedVehicle.vehicle_type, driver_id: selectedDriver.id, capacity_kg: 0 },
        driver: selectedDriver
      } : {
        id: null, company_name: "—",
        vehicle: { id: selectedVehicle.id, registration_number: selectedVehicle.registration_number, vehicle_number: selectedVehicle.vehicle_number, vehicle_type: selectedVehicle.vehicle_type, driver_id: selectedDriver.id, capacity_kg: 0 },
        driver: selectedDriver
      });
    }
    if (step === steps.length - 2) {
      const ok = await submitConsignmentToApiAndPdf();
      if (!ok) return;
      setStep(s => s + 1);
    } else {
      setStep(s => s + 1);
    }
  };

  const canNavigateBack = step !== 0 && step < steps.length - 1;

  function handleDownloadPdf() {
    if (pdfUrl) { window.open(pdfUrl, "_blank"); } else { alert("PDF not available"); }
  }

  function handleSharePdf() {
    if (!pdfUrl) return;
    if (navigator.share) { navigator.share({ title: "Consignment Invoice", url: pdfUrl }).catch(() => { }); }
    else { setShowShareOptions(true); }
  }

  function handleNewConsignment() {
    setStep(0); setCompany(null); setSender(null); setReceiver(null); setSingleTransporter(null);
    setConsignmentDetails({ origin: "", destination: "", description: "" });
    setItems([{ name: "", qty: "", weight: "", ratePerQtl: "" }]);
    setPaymentType("paid"); setSubmittedData(null);
    setSelectedDate(new Date().toISOString().slice(0, 10));
    setSubject(""); setPaymentPayableConsignor("sender"); setPdfUrl(null); setShowInvoiceModal(false);
    setHasBroker(false); setBrokerPaidBy(null);
    setSelectedTransporterMock(null); setSelectedVehicle(null); setSelectedDriver(null);
    // ✅ Reset invoice number
    setInvoiceNumber("");
  }

  const updateItem = (idx: number, key: keyof Item, value: string) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: value } : it));
  };

  const itemTotal = (item: Item) => (parseFloat(item.qty) || 0) * (parseFloat(item.ratePerQtl) || 0);
  const grandTotal = items.reduce((acc, it) => acc + itemTotal(it), 0);

  const dropdownCls = "w-full border-2 border-blue-300 rounded-lg px-4 py-3 text-base text-blue-900 bg-white font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 transition appearance-none cursor-pointer";

  return (
    <div className="min-h-screen w-full flex items-start justify-center py-8 px-2 bg-gradient-to-br from-[#cfdbff] via-[#edf1fe] to-[#edf1fe]">
      <div className="w-full max-w-screen-2xl mx-auto flex flex-col md:flex-row overflow-hidden rounded-2xl shadow-2xl border-2 border-[#456bcb] min-h-screen md:min-h-[720px]">

        {/* Sidebar */}
        <div className="hidden md:flex flex-col w-64 shrink-0 bg-white py-12 px-8 gap-2 border-r-2 border-[#456bcb]">
          <p className="text-[#456bcb] text-sm font-extrabold uppercase tracking-widest mb-8">Progress</p>
          <div className="space-y-2">
            {steps.map((label, idx) => {
              const done = idx < step;
              const active = idx === step;
              return (
                <button key={label} type="button" onClick={() => done && canNavigateBack && setStep(idx)} disabled={step >= 7}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all border-l-4 ${active ? "bg-white border-blue-600 shadow-[0_2px_8px_rgba(47,87,181,0.13)]" : done ? "bg-blue-50 hover:bg-blue-100 border-blue-300 cursor-pointer" : "bg-transparent border-transparent cursor-default"}`}>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-base font-extrabold shrink-0 border-2 ${active ? "bg-blue-700 text-white border-blue-700 shadow" : done ? "bg-white text-blue-800 border-blue-300" : "bg-blue-200 text-blue-300 border-blue-100"}`}>
                    {done ? <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg> : idx + 1}
                  </span>
                  <span className={`text-base font-bold tracking-wide uppercase ${active ? "text-blue-800" : done ? "text-blue-600" : "text-blue-400"}`}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0 bg-transparent">
          {/* Header */}
          <div className="border-b-2 border-[#456bcb] px-10 py-6 bg-gradient-to-r from-[#ddeaff] to-[#f3f7fe] flex items-center gap-4">
            <p className="text-sm text-[#2c4174] font-semibold mt-1">
              Step <span className="text-blue-800 font-extrabold">{step + 1}</span> of <span className="text-blue-600">{steps.length}</span> — <span className="font-bold text-blue-700">{steps[step]}</span>
            </p>
            <div className="ml-auto flex items-center gap-2">
              <label className="font-bold text-blue-700">Date:</label>
              <input type="date" className="border-2 border-blue-300 rounded-lg px-2 py-1 font-bold bg-white text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
            </div>
          </div>

          {/* Mobile progress */}
          <div className="flex md:hidden gap-1 px-4 py-3 border-b-2 border-[#456bcb] bg-transparent overflow-x-auto">
            {steps.map((s, i) => <div key={s} className={`h-2 flex-1 rounded-full transition-all ${i <= step ? "bg-blue-600" : "bg-blue-200"}`} />)}
          </div>

          {/* Steps content */}
          <div className="flex-1 px-6 md:px-10 py-6 md:py-8 overflow-y-auto w-full rounded-md bg-gradient-to-tr from-white via-blue-50 to-[#ebf1fd]">

            {/* STEP 0: Driver, Vehicle & Invoice Number */}
            {step === 0 && (
              <div className="space-y-8 max-w-xl">
                <p className="text-lg font-extrabold text-blue-900">Select driver &amp; vehicle</p>
                <div className="space-y-2">
                  <label className="text-base font-bold text-blue-800">Driver <span className="text-blue-700">*</span></label>
                  <div className="relative">
                    <select className={dropdownCls} value={selectedDriver?.id ?? ""} onChange={e => {
                      const drv = drivers.find(d => d.id === Number(e.target.value)) || null;
                      setSelectedDriver(drv);
                      setSelectedVehicle(null);
                    }}>
                      <option value="">— Select a driver —</option>
                      {drivers.map(drv => (
                        <option key={drv.id} value={drv.id}>{drv.name}{drv.phone ? ` (${drv.phone})` : ""}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-blue-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </div>
                  {drivers.length === 0 && <p className="text-sm text-blue-400 italic">Loading drivers…</p>}
                </div>

                {selectedDriver && (
                  <div className="space-y-2">
                    <label className="text-base font-bold text-blue-800">Vehicle <span className="text-blue-700">*</span></label>
                    <div className="relative">
                      <select className={dropdownCls} value={selectedVehicle?.id ?? ""} onChange={e => {
                        const veh = vehiclesForDriver.find((v: any) => v.id === Number(e.target.value)) || null;
                        setSelectedVehicle(veh);
                      }}>
                        <option value="">— Select a vehicle —</option>
                        {vehiclesForDriver.map((v: any) => (
                          <option key={v.id} value={v.id}>{v.registration_number || v.vehicle_number}{v.vehicle_type ? ` — ${v.vehicle_type}` : ""}</option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-blue-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
                      </span>
                    </div>
                    {vehiclesForDriver.length === 0 && <p className="text-sm text-blue-400 italic">No vehicles found.</p>}
                  </div>
                )}

                {selectedDriver && selectedVehicle && (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl px-5 py-4 flex flex-col gap-1 text-sm">
                    <p className="font-extrabold text-blue-800 mb-1">Selected</p>
                    <p className="text-blue-700"><span className="font-bold">Driver:</span> {selectedDriver.name}{selectedDriver.phone ? ` (${selectedDriver.phone})` : ""}</p>
                    <p className="text-blue-700"><span className="font-bold">Vehicle:</span> {selectedVehicle.registration_number || selectedVehicle.vehicle_number}{selectedVehicle.vehicle_type ? ` — ${selectedVehicle.vehicle_type}` : ""}</p>
                  </div>
                )}

                {/* ✅ NEW: Invoice / LR Number input */}
                <div className="space-y-2">
                  <label className="text-base font-bold text-blue-800">
                    Invoice / LR Number
                    <span className="ml-2 text-blue-400 font-normal text-sm">(optional — auto-generated if blank)</span>
                  </label>
                  <input
                    className="w-full border-2 border-blue-300 rounded-lg px-4 py-3 text-base text-blue-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition font-semibold tracking-widest uppercase placeholder:normal-case placeholder:tracking-normal placeholder:font-normal"
                    placeholder="e.g. LR-2024-00123"
                    value={invoiceNumber}
                    onChange={e => setInvoiceNumber(e.target.value.toUpperCase())}
                    maxLength={40}
                  />
                  {invoiceNumber.trim() ? (
                    <p className="text-xs text-blue-600 font-semibold flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                      LR No. will be: <span className="font-mono font-black text-blue-800">{invoiceNumber.trim()}</span>
                    </p>
                  ) : (
                    <p className="text-xs text-blue-400 italic">Leave blank to auto-generate from origin, destination &amp; vehicle.</p>
                  )}
                </div>

              </div>
            )}

            {/* STEP 1: Company */}
            {step === 1 && (
              <div className="space-y-4">
                <p className="text-lg font-extrabold text-blue-900 mb-3">Select your company</p>
                <div className="flex flex-col">
                  <input type="text" value={companySearch} onChange={e => setCompanySearch(e.target.value)} className="mb-2 px-4 py-2 border-2 border-blue-300 rounded-lg bg-white font-semibold text-blue-900 text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Search company by name, city or GST..." />
                  <div className="max-h-64 overflow-y-auto flex flex-col gap-2 custom-scrollbar">
                    {companiesMock.filter(c => (c.company_name + " " + c.city + " " + (c.gst_number || "")).toLowerCase().includes(companySearch.toLowerCase())).map(c => (
                      <button key={c.id} type="button" onClick={() => setCompany(c)} className={`w-full flex items-center justify-between px-4 py-2 rounded-lg border-2 transition-all font-semibold text-base ${company && company.id === c.id ? "border-blue-700 bg-white shadow" : "border-blue-100 bg-blue-50"}`} style={{ minHeight: 0 }}>
                        <div className="truncate max-w-xs text-left flex flex-col">
                          <span className={`font-bold text-base truncate ${company && company.id === c.id ? "text-blue-800" : "text-blue-900"}`}>{c.company_name}</span>
                          <span className={`text-xs truncate ${company && company.id === c.id ? "text-blue-500" : "text-blue-500/80"}`}>{c.city} &middot; GST: {c.gst_number}</span>
                        </div>
                        {company && company.id === c.id && <span className="ml-2 text-blue-700 shrink-0"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>}
                      </button>
                    ))}
                    {companiesMock.filter(c => (c.company_name + " " + c.city + " " + (c.gst_number || "")).toLowerCase().includes(companySearch.toLowerCase())).length === 0 && <div className="text-blue-400 italic text-sm py-2">No matching company found.</div>}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: Sender */}
            {step === 2 && (
              <div className="space-y-4">
                <p className="text-lg font-extrabold text-blue-900 mb-3">Select sender</p>
                <div className="flex flex-col">
                  <input type="text" value={senderSearch} onChange={e => setSenderSearch(e.target.value)} className="mb-2 px-4 py-2 border-2 border-blue-300 rounded-lg bg-white font-semibold text-blue-900 text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Search sender by name, phone, address..." />
                  <div className="max-h-64 overflow-y-auto flex flex-col gap-2 custom-scrollbar">
                    {sendersMock.filter(s => (s.name + " " + s.phone + " " + s.address).toLowerCase().includes(senderSearch.toLowerCase())).map(s => (
                      <button key={s.id} type="button" onClick={() => setSender(s)} className={`w-full flex items-center justify-between px-4 py-2 rounded-lg border-2 transition-all font-semibold text-base ${sender && sender.id === s.id ? "border-blue-700 bg-white shadow" : "border-blue-100 bg-blue-50"}`} style={{ height: 45 }}>
                        <div className="flex flex-row items-center gap-3 w-full truncate">
                          <span className={`font-bold text-base truncate ${sender && sender.id === s.id ? "text-blue-800" : "text-blue-900"}`}>{s.name}</span>
                          <span className={`text-xs truncate ${sender && sender.id === s.id ? "text-blue-500" : "text-blue-600/80"}`}>{s.phone} &middot; {s.address}</span>
                        </div>
                        {sender && sender.id === s.id && <span className="ml-2 text-blue-700 shrink-0"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>}
                      </button>
                    ))}
                    {sendersMock.filter(s => (s.name + " " + s.phone + " " + s.address).toLowerCase().includes(senderSearch.toLowerCase())).length === 0 && <div className="text-blue-400 italic text-sm py-2">No matching sender found.</div>}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Receiver */}
            {step === 3 && (
              <div className="space-y-4">
                <p className="text-lg font-extrabold text-blue-900 mb-3">Select receiver</p>
                <div className="flex flex-col">
                  <input type="text" value={receiverSearch} onChange={e => setReceiverSearch(e.target.value)} className="mb-2 px-4 py-2 border-2 border-blue-300 rounded-lg bg-white font-semibold text-blue-900 text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Search receiver by name, phone, address..." />
                  <div className="max-h-64 overflow-y-auto flex flex-col gap-2 custom-scrollbar">
                    {receiversMock.filter(r => (r.name + " " + r.phone + " " + r.address).toLowerCase().includes(receiverSearch.toLowerCase())).map(r => (
                      <button key={r.id} type="button" onClick={() => setReceiver(r)} className={`w-full flex items-center justify-between px-4 py-2 rounded-lg border-2 transition-all font-semibold text-base ${receiver && receiver.id === r.id ? "border-blue-700 bg-white shadow" : "border-blue-100 bg-blue-50"}`} style={{ height: 45 }}>
                        <div className="flex flex-row items-center gap-3 w-full truncate">
                          <span className={`font-bold text-base truncate ${receiver && receiver.id === r.id ? "text-blue-800" : "text-blue-900"}`}>{r.name}</span>
                          <span className={`text-xs truncate ${receiver && receiver.id === r.id ? "text-blue-500" : "text-blue-600/80"}`}>{r.phone} &middot; {r.address}</span>
                        </div>
                        {receiver && receiver.id === r.id && <span className="ml-2 text-blue-700 shrink-0"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>}
                      </button>
                    ))}
                    {receiversMock.filter(r => (r.name + " " + r.phone + " " + r.address).toLowerCase().includes(receiverSearch.toLowerCase())).length === 0 && <div className="text-blue-400 italic text-sm py-2">No matching receiver found.</div>}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: Details */}
            {step === 4 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="flex flex-col gap-2">
                  <label className="text-base font-bold text-blue-800">Origin Address <span className="text-blue-700">*</span></label>
                  <input className="border-2 border-blue-300 rounded-lg px-4 py-3 text-base text-blue-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition font-semibold" placeholder="e.g. Mumbai Warehouse, Andheri" value={consignmentDetails.origin} onChange={e => setConsignmentDetails((d: any) => ({ ...d, origin: e.target.value }))} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-base font-bold text-blue-800">Destination Address <span className="text-blue-700">*</span></label>
                  <input className="border-2 border-blue-300 rounded-lg px-4 py-3 text-base text-blue-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition font-semibold" placeholder="e.g. Delhi Distribution Hub" value={consignmentDetails.destination} onChange={e => setConsignmentDetails((d: any) => ({ ...d, destination: e.target.value }))} />
                </div>
                <div className="sm:col-span-2 flex flex-col gap-2">
                  <label className="text-base font-bold text-blue-800">Description <span className="text-blue-400 font-normal">(optional)</span></label>
                  <textarea className="border-2 border-blue-300 rounded-lg px-4 py-3 text-base text-blue-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition resize-none font-semibold" rows={3} placeholder="Briefly describe this consignment..." value={consignmentDetails.description} onChange={e => setConsignmentDetails((d: any) => ({ ...d, description: e.target.value }))} />
                </div>
                <div className="sm:col-span-2 flex flex-col gap-2">
                  <label className="text-base font-bold text-blue-800">Invoice Subject <span className="text-blue-700">*</span></label>
                  <input className="border-2 border-blue-300 rounded-lg px-4 py-3 text-base text-blue-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition font-semibold" value={subject} placeholder="eg. Subject to Pune Jurisdiction" onChange={e => setSubject(e.target.value)} />
                  <div className="text-xs text-blue-500 pt-1">This will appear on your LR/Invoice preview.</div>
                </div>
                <div className="sm:col-span-2 flex gap-2 items-center mt-4">
                  <label className="font-bold text-blue-800">Consignment Date:</label>
                  <input type="date" className="border-2 border-blue-300 rounded-lg px-2 py-1 font-bold bg-white text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
                </div>
              </div>
            )}

            {/* STEP 5: Items */}
            {step === 5 && (
              <div className="space-y-6">
                <p className="text-lg font-extrabold text-blue-900">Add items to this consignment</p>
                {items.map((item, idx) => {
                  const rowTotal = itemTotal(item);
                  return (
                    <div key={idx} className="border-2 border-blue-200 bg-white rounded-2xl p-6 space-y-4 shadow-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-base font-bold text-blue-900 uppercase tracking-wide">Item {idx + 1}</span>
                        {idx > 0 && (
                          <button type="button" onClick={() => setItems(p => p.filter((_, i) => i !== idx))} className="text-base text-red-600 hover:text-white hover:bg-red-600 px-3 py-1 rounded-lg border-2 border-red-600 font-bold tracking-wider transition">Remove</button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                        <div className="col-span-2 md:col-span-4 flex flex-col gap-1.5">
                          <label className="text-sm text-blue-900 font-bold">Item Name *</label>
                          <input className="border-2 border-blue-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold" placeholder="e.g. Wheat, Cotton, Rice" value={item.name} onChange={e => updateItem(idx, "name", e.target.value)} />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-sm text-blue-900 font-bold">Qty <span className="text-blue-400 font-normal">(Qtl)</span></label>
                          <input type="number" min={0} className="border-2 border-blue-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold" placeholder="0" value={item.qty} onChange={e => updateItem(idx, "qty", e.target.value)} />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-sm text-blue-900 font-bold">Weight <span className="text-blue-400 font-normal">(Qtl)</span></label>
                          <input type="number" min={0} className="border-2 border-blue-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold" placeholder="0" value={item.weight} onChange={e => updateItem(idx, "weight", e.target.value)} />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-sm text-blue-900 font-bold">Rate / Qtl <span className="text-blue-400 font-normal">(₹)</span></label>
                          <input type="number" min={0} className="border-2 border-blue-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold" placeholder="₹ 0" value={item.ratePerQtl} onChange={e => updateItem(idx, "ratePerQtl", e.target.value)} />
                        </div>
                        <div className="flex flex-col justify-end gap-1.5">
                          <label className="text-sm text-blue-900 font-bold invisible">Total</label>
                          <div className={`rounded-lg px-3 py-2 text-base font-bold border-2 ${rowTotal > 0 ? "bg-blue-50 border-blue-300 text-blue-900" : "bg-gray-50 border-gray-200 text-gray-400"}`}>
                            {rowTotal > 0 ? `₹${rowTotal.toLocaleString("en-IN")}` : "—"}
                          </div>
                        </div>
                      </div>
                      {parseFloat(item.qty) > 0 && parseFloat(item.ratePerQtl) > 0 && (
                        <p className="text-xs text-blue-500 text-right">{parseFloat(item.qty)} Qtl × ₹{parseFloat(item.ratePerQtl).toLocaleString("en-IN")} = <span className="font-bold text-blue-700">₹{rowTotal.toLocaleString("en-IN")}</span></p>
                      )}
                    </div>
                  );
                })}
                <button type="button" onClick={() => setItems(p => [...p, { name: "", qty: "", weight: "", ratePerQtl: "" }])} className="w-full border-2 border-dashed border-blue-400 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-2xl py-4 text-base font-extrabold transition text-center">+ Add Another Item</button>
                {grandTotal > 0 && (
                  <div className="flex justify-end">
                    <div className="bg-blue-700 text-white rounded-xl px-6 py-3 text-base font-extrabold">Total Declared Value: ₹{grandTotal.toLocaleString("en-IN")}</div>
                  </div>
                )}
                <div className="border-t-2 border-blue-100 pt-5 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer w-fit">
                    <input type="checkbox" checked={hasBroker} onChange={e => { setHasBroker(e.target.checked); if (!e.target.checked) setBrokerPaidBy(null); }} className="w-5 h-5 accent-blue-700 cursor-pointer" />
                    <span className="text-base font-bold text-blue-900">Is there a broker involved?</span>
                  </label>
                  {hasBroker && (
                    <div className="ml-8 space-y-2">
                      <p className="text-sm font-semibold text-blue-700">Broker commission paid by:</p>
                      <div className="flex gap-4">
                        {(["receiver", "sender"] as const).map((party) => (
                          <label key={party} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 text-base font-bold cursor-pointer transition-all ${brokerPaidBy === party ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-blue-200 text-blue-900 hover:border-blue-500"}`}>
                            <input type="radio" name="brokerPaidBy" value={party} checked={brokerPaidBy === party} onChange={() => setBrokerPaidBy(party)} className="hidden" />
                            {party.charAt(0).toUpperCase() + party.slice(1)}
                            {party === "receiver" && receiver?.name && <span className="text-xs font-normal opacity-75 ml-1">({receiver.name})</span>}
                            {party === "sender" && sender?.name && <span className="text-xs font-normal opacity-75 ml-1">({sender.name})</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 6: Payment */}
            {step === 6 && (
              <div className="space-y-6 max-w-xl">
                <p className="text-lg font-extrabold text-blue-900 mb-3">Select payment mode</p>
                {[
                  { value: "paid", label: "Paid", desc: "Payment already received" },
                  { value: "to_pay", label: "To Pay", desc: "Receiver pays on delivery" },
                  { value: "credit", label: "Credit", desc: "Pay later on credit terms" },
                ].map(opt => (
                  <button key={opt.value} type="button" onClick={() => setPaymentType(opt.value)} className={`w-full text-left px-5 py-4 rounded-lg border-2 font-extrabold text-lg transition-all ${paymentType === opt.value ? "border-blue-600 bg-white shadow" : "border-blue-100 bg-blue-50"}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-base font-extrabold ${paymentType === opt.value ? "text-blue-700" : "text-blue-900"}`}>{opt.label}</p>
                        <p className={`text-xs ${paymentType === opt.value ? "text-blue-400" : "text-blue-600/80"}`}>{opt.desc}</p>
                      </div>
                      {paymentType === opt.value && <span className="w-6 h-6 rounded-full text-blue-700 flex items-center justify-center border-2 border-blue-700 bg-white shadow"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>}
                    </div>
                  </button>
                ))}
                <div className="mt-3 flex items-center gap-4 text-blue-700 font-extrabold text-base">
                  <span>Who is paying?</span>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" name="who_pay" checked={paymentPayableConsignor === "sender"} onChange={() => setPaymentPayableConsignor("sender")} className="form-radio text-blue-600" />
                    Sender {sender?.name && `(${sender.name})`}
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" name="who_pay" checked={paymentPayableConsignor === "receiver"} onChange={() => setPaymentPayableConsignor("receiver")} className="form-radio text-blue-600" />
                    Receiver {receiver?.name && `(${receiver.name})`}
                  </label>
                </div>
                <p className="text-sm text-blue-600 pt-2 font-bold">
                  LR number: <span className="font-mono font-black text-blue-800">{invoiceNumber.trim() || "Will be auto-generated on submission"}</span>
                </p>
                <div className="pt-3 flex gap-2 items-center">
                  <label className="font-bold text-blue-700">Payment/Booking Date:</label>
                  <input type="date" className="border-2 border-blue-300 rounded-lg px-2 py-1 font-bold bg-white text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
                </div>
              </div>
            )}

            {/* STEP 7: Invoice summary */}
            {step === 7 && submittedData && (
              <div className="space-y-6">
                <div className="flex items-center gap-4 bg-blue-50 border-2 border-blue-200 rounded-2xl px-7 py-6">
                  <span className="text-3xl text-green-600">✓</span>
                  <div>
                    <p className="font-extrabold text-blue-900 text-xl">Consignment Created!</p>
                    <p className="text-lg text-blue-800">LR Number: <span className="font-mono font-black text-blue-700 text-xl">{submittedData.invoiceId}</span></p>
                  </div>
                </div>
                <div className="border-2 border-blue-200 rounded-2xl overflow-hidden text-base font-bold">
                  <div className="bg-blue-100 px-7 py-3 border-b-2 border-blue-200 flex justify-between">
                    <span className="font-extrabold text-blue-800 tracking-wider">Invoice Summary</span>
                    <span className="text-blue-800">{selectedDate}</span>
                  </div>
                  {[
                    { label: "Transporter", value: `${submittedData.singletransporter?.company_name}` },
                    { label: "Driver", value: selectedDriver?.name },
                    { label: "Vehicle", value: selectedVehicle?.registration_number || selectedVehicle?.vehicle_number },
                    { label: "Company", value: submittedData.company?.company_name },
                    { label: "Sender", value: `${submittedData.sender?.name} (${submittedData.sender?.city})` },
                    { label: "Receiver", value: `${submittedData.receiver?.name} (${submittedData.receiver?.city})` },
                    { label: "Route", value: `${submittedData.consignmentDetails.origin} → ${submittedData.consignmentDetails.destination}` },
                    { label: "Items", value: submittedData.items.filter((i: any) => i.name).map((i: any) => i.name).join(", ") },
                    { label: "Subject", value: (submittedData.subject || subject) },
                    { label: "EWAY BILL/LR NO.", value: submittedData.ewaybillno },
                    { label: "Payment", value: submittedData.paymentType === "paid" ? "Paid" : submittedData.paymentType === "to_pay" ? "To Pay" : "Credit" },
                    { label: "Pay By", value: submittedData.paymentPayableConsignorId === submittedData.sender?.id ? `Sender (${submittedData.sender?.name})` : submittedData.paymentPayableConsignorId === submittedData.receiver?.id ? `Receiver (${submittedData.receiver?.name})` : "—" },
                    { label: "Date", value: selectedDate },
                    // ✅ Show broker info in summary if applicable
                    ...(hasBroker ? [{ label: "Broker Paid By", value: brokerPaidBy === "sender" ? `Sender (${sender?.name})` : brokerPaidBy === "receiver" ? `Receiver (${receiver?.name})` : "—" }] : []),
                  ].map(row => (
                    <div key={row.label} className="flex px-7 py-4 border-b border-blue-100/80 last:border-0 bg-white">
                      <span className="text-blue-400 w-32 shrink-0">{row.label}</span>
                      <span className="text-blue-900 font-bold">{row.value || "—"}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4">
                  <button type="button" onClick={handleNewConsignment} className="flex-1 border-2 border-blue-600 rounded-xl py-3 text-base font-bold text-blue-700 hover:bg-blue-100 transition">New Consignment</button>
                  <button type="button" className="flex-1 bg-blue-700 hover:bg-white text-white hover:text-blue-700 rounded-xl py-3 text-base font-extrabold transition border-2 border-blue-700" onClick={() => setShowInvoiceModal(true)}>View/Download Invoice PDF</button>
                </div>
              </div>
            )}

            {/* Navigation */}
            {step < 7 && (
              <div className="border-t-2 border-[#456bcb] px-10 py-6 flex justify-between items-center bg-gradient-to-r from-[#ddeaff] to-[#f3f7fe] font-extrabold mt-2 flex-col gap-3 sm:flex-row sm:gap-0 sm:justify-between sm:items-center">
                <button type="button" onClick={() => setStep(s => s - 1)} disabled={step === 0} className="px-7 py-3 bg-white rounded-xl border-2 border-blue-400 text-base font-bold text-blue-700 hover:bg-blue-50 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm">Back</button>
                <button type="button" onClick={handleNext} disabled={!isStepValid()} className={`px-10 py-3 rounded-xl text-base font-extrabold transition tracking-wider shadow ${step === steps.length - 2 ? "bg-blue-700 hover:bg-white text-white hover:text-blue-700 border-2 border-blue-700" : "bg-white hover:bg-blue-700 text-blue-700 hover:text-white border-2 border-blue-700"} disabled:opacity-50 disabled:cursor-not-allowed`}>{step === steps.length - 2 ? "Generate Invoice" : "Next →"}</button>
              </div>
            )}

            {/* Invoice Modal */}
            {showInvoiceModal && (
              <div className="fixed z-50 inset-0 flex items-center justify-center bg-blue-900/30 backdrop-blur-md transition-all">
                <div className="bg-white rounded-xl shadow-2xl border-4 border-blue-700 max-w-2xl w-full relative flex flex-col items-stretch sm:max-w-2xl sm:min-w-[360px] sm:mx-2 mx-2 min-w-0" style={{ maxHeight: "95vh" }}>
                  <button className="absolute top-3 right-3 bg-blue-700 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-800 transition" onClick={() => setShowInvoiceModal(false)}>✕</button>
                  <div className="pt-8 pb-4 px-2 sm:px-6 w-full">
                    <h2 className="text-lg font-bold text-blue-700 text-center mb-2">Invoice Preview</h2>
                    <div className="w-full" style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #c9d5f8", background: "#fff", margin: "0 auto" }}>
                      <div ref={previewPanelRef} style={{ padding: 8, background: "#fff", width: "100%", minWidth: 0, maxWidth: "794px", margin: "0 auto", boxSizing: "border-box" }} className="overflow-x-auto">
                        <div className="inline-block" style={{ width: "100%", minWidth: "320px", maxWidth: "794px" }}>
                          <InvoicePreviewPanel company={company} sender={sender} receiver={receiver} selectedDate={selectedDate} transporter={singletransporter} selectedTransport={selectedTransporterMock} selectedDriver={selectedDriver} selectedVehicle={selectedVehicle} consignmentDetails={consignmentDetails} items={items} paymentType={paymentType} step={7} submittedData={submittedData} subject={subject} invoiceNumber={invoiceNumber} />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center w-full">
                      <button className="bg-blue-700 text-white px-4 py-2 sm:px-6 sm:py-2 rounded-lg font-bold border-2 border-blue-700 hover:bg-white hover:text-blue-700 transition w-full sm:w-auto" onClick={handleDownloadPdf}>Download PDF</button>
                      <button className="bg-blue-400 text-white px-4 py-2 sm:px-6 sm:py-2 rounded-lg font-bold border-2 border-blue-500 hover:bg-white hover:text-blue-600 transition w-full sm:w-auto" onClick={handleSharePdf}>Share</button>
                      <a className="bg-green-600 text-white px-4 py-2 sm:px-6 sm:py-2 rounded-lg font-bold border-2 border-green-700 hover:bg-white hover:text-green-700 hover:underline transition w-full sm:w-auto text-center" href={pdfUrl || "#"} target="_blank" rel="noopener noreferrer" download={`invoice_${submittedData?.invoiceId}.pdf`}>Open PDF Link</a>
                    </div>
                  </div>
                  {showShareOptions && (
                    <div className="mt-4 border-t pt-4 px-2 sm:px-6">
                      <div className="mb-2 text-sm font-bold text-blue-700">Share invoice:</div>
                      <input className="border px-2 py-1 w-full rounded text-xs bg-blue-50 text-blue-900" value={pdfUrl || ""} readOnly onFocus={e => e.target.select()} />
                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <button className="bg-blue-700 text-white px-4 py-2 rounded font-bold text-xs w-full sm:w-auto" onClick={() => { if (pdfUrl) { navigator.clipboard.writeText(pdfUrl); alert("Copied to clipboard."); } }}>Copy Link</button>
                        <button className="px-2 py-2 text-xs text-red-500 underline w-full sm:w-auto" onClick={() => setShowShareOptions(false)}>Close</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Live preview panel */}
        <div className="lg:flex flex-col mb-[20px] md:mb-0 md:m-6 w-[100%] md:w-[42%] shrink-0 border-l-2 border-[#456bcb] bg-gradient-to-b from-white to-[#dce7fb] px-6 py-6 overflow-y-auto shadow-inner rounded-tr-2xl rounded-br-2xl">
          <div style={{ position: "relative" }}>
            <div ref={previewPanelRef} style={{ width: "100%", maxWidth: "100%", minWidth: "100%", background: "#fff", boxSizing: "border-box" }}>
              <InvoicePreviewPanel company={company} sender={sender} receiver={receiver} transporter={singletransporter} selectedTransport={selectedTransporterMock} selectedDriver={selectedDriver} selectedVehicle={selectedVehicle} consignmentDetails={consignmentDetails} selectedDate={selectedDate} items={items} paymentType={paymentType} step={step} submittedData={submittedData} subject={subject} invoiceNumber={invoiceNumber} />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AddConsignment;